import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface SessionEngagementBucket {
    period?: string;
    period_start?: string;
    engaged_sessions?: number;
    low_engagement_sessions?: number;
}

interface Props {
    data?: SessionEngagementBucket[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const formatPeriodLabel = (date: Date, period: Props['period'], fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const SessionEngagementChart: React.FC<Props> = ({ data = [], period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const engaged: number[] = [];
            const lowEngagement: number[] = [];

            data.forEach(item => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start || '');
                    }
                } catch {
                    date = new Date();
                }

                categories.push(formatPeriodLabel(date, period, item.period || ''));
                engaged.push(Number(item.engaged_sessions) || 0);
                lowEngagement.push(Number(item.low_engagement_sessions) || 0);
            });

            const option: ECOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: (params: any) => {
                        if (!Array.isArray(params)) {
                            return '';
                        }

                        const index = params[0]?.dataIndex || 0;
                        const engagedCount = engaged[index] || 0;
                        const lowCount = lowEngagement[index] || 0;
                        const total = engagedCount + lowCount;
                        const engagedRate = total > 0 ? ((engagedCount / total) * 100).toFixed(1) : '0.0';
                        const lowRate = total > 0 ? ((lowCount / total) * 100).toFixed(1) : '0.0';

                        return [
                            params[0]?.axisValue || '',
                            `Engaged sessions: ${engagedCount.toLocaleString()} (${engagedRate}%)`,
                            `Low engagement: ${lowCount.toLocaleString()} (${lowRate}%)`,
                            `Total sessions: ${total.toLocaleString()}`,
                        ].join('<br/>');
                    },
                },
                legend: {
                    data: ['Engaged sessions', 'Low engagement'],
                    bottom: 0,
                },
                grid: {
                    left: '6%',
                    right: '6%',
                    top: '12%',
                    bottom: 70,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: { rotate: period === 'daily' ? 45 : 0 },
                },
                yAxis: {
                    type: 'value',
                    name: 'Sessions',
                },
                series: [
                    {
                        name: 'Engaged sessions',
                        type: 'bar',
                        data: engaged,
                        itemStyle: { color: '#52c41a' },
                        barMaxWidth: 30,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: (params: any) => Number(params.value || 0).toLocaleString(),
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Low engagement',
                        type: 'bar',
                        data: lowEngagement,
                        itemStyle: { color: '#faad14' },
                        barMaxWidth: 30,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: (params: any) => Number(params.value || 0).toLocaleString(),
                            fontSize: 11,
                        },
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 380 }} />;
};

export default SessionEngagementChart;
